import React, { ReactNode } from 'react';
import { Box, CardMedia, Dialog, DialogContent, IconButton, Skeleton, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useAppSelector } from "../../hooks/hooks";
import HereMapTourComponent from './HereMapTourComponent';
import { generateTourWaypointEntries } from '../../utils/hereMapUtils';

interface TourCardProps {
    children?: ReactNode;
    highlightedStayId?: number | null;
    hoveredStayId?: number | null;
    onHoveredStayChange?: (stayId: number | null) => void;
}

export default function TourSidebar({ children, highlightedStayId = null, hoveredStayId = null, onHoveredStayChange }: TourCardProps) {
    const HERE_MAP_ID = process.env.REACT_APP_HERE_MAP_ID!;
    const chosenTourDetail = useAppSelector(state => state.tour.choosedTourDetail);
    const waypoints = useAppSelector(state => state.tour.generatedCoordinates);
    const loading = useAppSelector(state => state.tour.loading);
    const userEntitlements = useAppSelector((state) => state.user.entitlements);
    const vanliferId = useAppSelector((state) => state.user.vanliferId);
    const [mapDialogOpen, setMapDialogOpen] = React.useState(false);
    const waypointEntries = React.useMemo(
        () => (chosenTourDetail?.stays?.length ? generateTourWaypointEntries(chosenTourDetail.stays) : []),
        [chosenTourDetail?.stays]
    );
    const highlightedWaypointIndex = React.useMemo(() => {
        if (highlightedStayId == null) {
            return null;
        }

        const index = waypointEntries.findIndex((entry) => entry.stayId === highlightedStayId);

        return index >= 0 ? index : null;
    }, [highlightedStayId, waypointEntries]);
    const hoveredWaypointIndex = React.useMemo(() => {
        if (hoveredStayId == null) {
            return null;
        }

        const index = waypointEntries.findIndex((entry) => entry.stayId === hoveredStayId);

        return index >= 0 ? index : null;
    }, [hoveredStayId, waypointEntries]);
    const handleHoveredWaypointIndexChange = React.useCallback((index: number | null) => {
        if (!onHoveredStayChange) {
            return;
        }

        if (index == null) {
            onHoveredStayChange(null);
            return;
        }

        onHoveredStayChange(waypointEntries[index]?.stayId ?? null);
    }, [onHoveredStayChange, waypointEntries]);
    const canShowBigMap = React.useMemo(() => {
        const isOwnTour = !!chosenTourDetail?.vanliferId && !!vanliferId && chosenTourDetail.vanliferId === vanliferId;
        const hasEntitlement = userEntitlements.some((entitlement) => entitlement.featureId === 'vanlifershowbigmap' && entitlement.value === 'true');

        return isOwnTour && hasEntitlement;
    }, [chosenTourDetail?.vanliferId, userEntitlements, vanliferId]);

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', p: 1 }}>
                <CardMedia
                    sx={{
                        bgcolor: '#f0f0f0',
                        height: 260,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '8px',
                        overflow: 'hidden',
                    }}
                >
                    {loading ? (
                        <Skeleton variant="rounded" height={260} width="100%" />
                    ) : waypoints && chosenTourDetail?.tourId ? (
                        <HereMapTourComponent
                             apiKey={HERE_MAP_ID}
                             waypoints={waypoints}
                             height="260px"
                             editorSite={true}
                             tourId={chosenTourDetail.tourId}
                             highlightedWaypointIndex={highlightedWaypointIndex}
                             hoveredWaypointIndex={hoveredWaypointIndex}
                             onHoveredWaypointIndexChange={handleHoveredWaypointIndexChange}
                             waypointEntries={waypointEntries}
                             onExpand={() => setMapDialogOpen(true)}
                             showExpandButton={canShowBigMap}
                        />
                    ) : (
                        <Typography variant="subtitle1" color="text.secondary">
                            Keine Kartendaten verfügbar.
                        </Typography>
                    )}
                </CardMedia>

                {/* Additional sidebar content */}
                {children}
            </Box>
            <Dialog open={mapDialogOpen && canShowBigMap} onClose={() => setMapDialogOpen(false)} maxWidth="lg" fullWidth>
                <DialogContent sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <IconButton onClick={() => setMapDialogOpen(false)}>
                            <CloseRoundedIcon />
                        </IconButton>
                    </Box>
                    {waypoints && chosenTourDetail?.tourId && (
                        <HereMapTourComponent
                            apiKey={HERE_MAP_ID}
                            waypoints={waypoints}
                            height="70vh"
                            editorSite={true}
                            tourId={chosenTourDetail.tourId}
                            highlightedWaypointIndex={highlightedWaypointIndex}
                            hoveredWaypointIndex={hoveredWaypointIndex}
                            onHoveredWaypointIndexChange={handleHoveredWaypointIndexChange}
                            waypointEntries={waypointEntries}
                            showExpandButton={false}
                            showWaypointLabels
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
